import useTestId from '@ori/testid-generator';
import type { ReactNode, FC, ClipboardEvent } from 'react';
import React, { useEffect, useRef, useCallback } from 'react';

import { Localization } from '../../Localization';
import type { IErrorData } from '../../contexts';
import { getErrorLocalizationByCode } from '../../utils';
import { FormErrorSummary, StyledSubmitError } from '../FormValidationSummary';
import { ResendLink } from './ResendLink';
import { VerificationCodeInput } from './VerificationCodeInput';
import { CodeDiv, ResendText, RootContainer } from './VerificationStyledComponents';

interface IVerifyProps {
  handleSubmitVerification: (code: string) => void;
  handleResendCode: (event: React.MouseEvent) => void;
  invalidCodeError?: IErrorData;
  isSubmitting: boolean;
  isResendDisabled: boolean;
}

export const Verify: FC<IVerifyProps> = (props) => {
  const { handleSubmitVerification, handleResendCode, invalidCodeError, isResendDisabled, isSubmitting } = props;

  const codeInputRefs = useRef<HTMLInputElement[]>([]);

  const { getTestIdProps } = useTestId();

  const submitErrorMessage = getErrorLocalizationByCode(invalidCodeError?.errorCode);

  const mountOrUpdate = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy
    if (codeInputRefs.current && codeInputRefs.current.length > 0) {
      codeInputRefs.current[0]?.focus();
    }
  }, []);

  useEffect(() => {
    mountOrUpdate();
  }, [mountOrUpdate]);

  const onNumberDelete = (index: number) => {
    const newInputIndex = index - 1;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy
    if (newInputIndex >= 0 && codeInputRefs.current) {
      codeInputRefs.current[newInputIndex]?.focus();
    }
  };

  const setInputRef = (instance: HTMLInputElement, index: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy
    if (codeInputRefs.current) {
      codeInputRefs.current[index] = instance;
    }
  };

  const submitVerification = () => {
    let codeNumbers;

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/no-unnecessary-condition -- needed this condition
    if (codeInputRefs && codeInputRefs.current) {
      codeNumbers = codeInputRefs.current.map((input) => input.value);
    }
    if (codeNumbers?.every((value) => Boolean(value))) {
      const code = codeNumbers.join('');
      handleSubmitVerification(code);
    }
  };

  const onNumberInput = (index: number) => {
    const newInputIndex = index + 1;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy
    if (codeInputRefs.current && newInputIndex < codeInputRefs.current.length) {
      codeInputRefs.current[newInputIndex]?.focus();
    } else {
      submitVerification();
    }
  };
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pastedCode = event.clipboardData.getData('text/plain');
    const codeLength = codeInputRefs.current.length;
    // eslint-disable-next-line unicorn/prefer-spread -- we need split for this
    const codeArray = pastedCode.split('').slice(0, codeLength);
    codeInputRefs.current.forEach((codeInputRef, index) => {
      if (codeArray[index] && !Number.isNaN(Number(codeArray[index]))) {
        // eslint-disable-next-line no-param-reassign -- we need
        codeInputRef.value = codeArray[index] as string;
      }
    });

    if (codeInputRefs.current.length) {
      submitVerification();
    }
  };

  const renderVerificationCodeInput = (index: number): ReactNode => (
    <VerificationCodeInput
      key={index}
      index={index}
      handleInput={onNumberInput}
      handleDelete={onNumberDelete}
      inputRef={setInputRef}
      handlePaste={onPaste}
      isError={Boolean(invalidCodeError)}
      isSubmitting={isSubmitting}
    />
  );

  const renderVerificationCodeInputs = (): ReactNode => {
    const codeLength = 6;

    return (
      <CodeDiv>{Array.from({ length: codeLength }).map((_x, index) => renderVerificationCodeInput(index))}</CodeDiv>
    );
  };

  return (
    <RootContainer>
      <form>
        {renderVerificationCodeInputs()}
        {invalidCodeError ? (
          <FormErrorSummary {...getTestIdProps('verification-error-summary')}>
            <StyledSubmitError
              key={invalidCodeError.errorCode}
              error
            >
              <Localization {...submitErrorMessage} />
            </StyledSubmitError>
          </FormErrorSummary>
        ) : null}
        <ResendText
          paragraph
          align="center"
        >
          <ResendLink
            handleResendCode={handleResendCode}
            isResendDisabled={isResendDisabled}
          />
        </ResendText>
      </form>
    </RootContainer>
  );
};
